import { gql } from '../../graphql/client';
import { M_INSERT_APP_PACKAGE, Q_FETCH_ALL_APP_DATA } from '../../graphql/operations';
import type { AppPackage, PackageData } from '../../types/forge';

/**
 * Assemble a `package_data` snapshot from live Hasura state and insert
 * it as an `app_package` row.
 *
 * Mirrors Burrow's `app_packager.dart` flow: client-side assembly,
 * single insert, immutable thereafter.
 *
 * Phase 3 wires this to platform-service `POST /publish/env` so the env's
 * `active_package_id` is updated atomically with the new package row.
 */

interface AllAppData {
  FrontendApp_by_pk: {
    id: string;
    org_id: string;
    name: string;
    main_view_id: string;
    theme: unknown | null;
    app_instances: Array<{ key: string; value: string }>;
  } | null;
  View: Array<{
    id: string;
    Type: string;
    name: string;
    sort: number;
    mask_id: string | null;
    viewStyle: { id: string; properties: Record<string, unknown> } | null;
    viewSource: unknown | null;
    child_views: Array<{ View_id: string; related_view_id: string; sort: number }>;
    interactions: Array<{
      interaction: {
        type: string;
        Interaction_Actions: Array<{
          priority: number;
          Action: { Type: string; Value: string | null };
        }>;
      };
    }>;
  }>;
  InputMask: Array<{ id: string; name: string; pattern: string; placeholder: string | null }>;
}

export async function fetchAllAppData(appId: string): Promise<PackageData> {
  const data = await gql<AllAppData>(Q_FETCH_ALL_APP_DATA, { appId });
  if (!data.FrontendApp_by_pk) {
    throw new Error(`App not found: ${appId}`);
  }

  return {
    frontend_app: {
      id: data.FrontendApp_by_pk.id,
      name: data.FrontendApp_by_pk.name,
      org_id: data.FrontendApp_by_pk.org_id,
      main_view_id: data.FrontendApp_by_pk.main_view_id,
      theme: data.FrontendApp_by_pk.theme,
      app_instances: data.FrontendApp_by_pk.app_instances,
    },
    app_views: data.View.map((v) => ({
      id: v.id,
      Type: v.Type,
      name: v.name,
      mask_id: v.mask_id,
      viewStyle: v.viewStyle?.properties ?? null,
      viewSource: v.viewSource,
      child_views: v.child_views.map((c) => ({
        related_view_id: c.related_view_id,
        sort: c.sort,
      })),
      // Phase 5: embed interactions in the snapshot in the shape the
      // engine's runInteractions expects (InteractionDef[]).
      interactions: (v.interactions ?? []).map((vi) => ({
        type: vi.interaction.type,
        actions: (vi.interaction.Interaction_Actions ?? []).map((ia) => ({
          type: ia.Action.Type,
          value: ia.Action.Value ?? '',
          priority: ia.priority,
        })),
      })),
    })),
    masks: data.InputMask,
  };
}

export interface CreatePackageInput {
  appId: string;
  version: string;
  packageName?: string;
  platformVersion?: string;
}

export async function createAppPackage(input: CreatePackageInput): Promise<AppPackage> {
  const packageData = await fetchAllAppData(input.appId);
  const result = await gql<{ insert_app_package_one: AppPackage }>(M_INSERT_APP_PACKAGE, {
    appId: input.appId,
    version: input.version,
    packageName: input.packageName ?? null,
    platformVersion: input.platformVersion ?? null,
    packageData,
  });
  return result.insert_app_package_one;
}
